import { useState, useEffect } from 'react';
import { useHighlights, HIGHLIGHT_COLOR } from '../hooks/use-highlights';

function PreviewTab({ artifact }) {
  const isCode = ['code','js','jsx','ts','tsx','css','json','html','py','sql']
    .includes((artifact.artifact_type || '').toLowerCase());
  const isImage = artifact.artifact_type === 'Upload' && artifact.is_image;
  const isImageUrl = !isImage && (artifact.content || '').match(/^https?:\/\/.+\.(png|jpg|jpeg|gif|webp|svg)/i);
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
      {(isImage || isImageUrl) ? (
        <img src={artifact.content} alt={artifact.title || ''} style={{ maxWidth: '100%', borderRadius: 6 }} />
      ) : isCode ? (
        <pre style={{ color: 'var(--text-2)', fontSize: 13, lineHeight: 1.7, fontFamily: 'var(--font-mono)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
          {artifact.content || '(No content)'}
        </pre>
      ) : (
        <div style={{ color: 'var(--text-2)', fontSize: 14, lineHeight: 1.9, fontFamily: 'var(--font-serif)', whiteSpace: 'pre-wrap' }}>
          {artifact.content || '(No content)'}
        </div>
      )}
    </div>
  );
}

function CodeTab({ artifact }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard?.writeText(artifact.content || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '8px 14px', borderBottom: '1px solid var(--border-4)', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
        <button onClick={copy} style={{ background: 'none', border: '1px solid var(--border-2)', borderRadius: 5, color: 'var(--text-5)', fontSize: 11, fontFamily: 'var(--font-serif)', padding: '3px 10px', cursor: 'pointer' }}>
          {copied ? 'copied ✓' : 'copy all'}
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
        <pre style={{ color: 'var(--text-2)', fontSize: 12, lineHeight: 1.7, fontFamily: 'var(--font-mono)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
          {artifact.content || '(No content)'}
        </pre>
      </div>
    </div>
  );
}

export default function ThreadArtifactPanel({ userId, supabase, currentSessionId, isSpace, width = 280, onArtifactSelect, highlightRefreshKey = 0 }) {
  const [activeTab, setActiveTab] = useState('artifacts');
  const [threadArtifacts, setThreadArtifacts] = useState([]);
  const [pinnedArtifacts, setPinnedArtifacts] = useState([]);
  const [recentArtifacts, setRecentArtifacts] = useState([]);
  const [selectedArtifact, setSelectedArtifact] = useState(null);
  const [viewerTab, setViewerTab] = useState('preview');
  const { highlights, loadHighlights, deleteHighlight } = useHighlights({ userId, supabase });
  const [hoveredHighlight, setHoveredHighlight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attachments, setAttachments] = useState([]);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [feedbackRating, setFeedbackRating] = useState(null);
  const [feedbackNote, setFeedbackNote] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackSending, setFeedbackSending] = useState(false);

  useEffect(() => {
    if (!userId || !supabase) return;
    async function load() {
      setLoading(true);

      // This thread/space
      if (currentSessionId) {
        // Load artifacts via API (RLS-safe)
        try {
          const { data: { session: authSession } } = await supabase.auth.getSession();
          const url = isSpace
            ? `/api/user/artifacts?sessionId=${encodeURIComponent(currentSessionId)}`
            : `/api/user/artifacts?sessionId=${encodeURIComponent(currentSessionId)}&mine=true`;
          const artRes = await fetch(url, {
            headers: { 'Authorization': `Bearer ${authSession?.access_token || ''}` },
          });
          if (artRes.ok) {
            const { artifacts: threadData } = await artRes.json();
            setThreadArtifacts(threadData || []);
          }
        } catch (_) { setThreadArtifacts([]); }
      }

      // Only show this thread's artifacts — no cross-thread pinned/recent
      setPinnedArtifacts([]);
      setRecentArtifacts([]);

      await loadHighlights(currentSessionId);
      setLoading(false);
    }
    load();
  }, [userId, supabase, currentSessionId, activeTab, highlightRefreshKey]);

  const TYPE_COLORS = {
    web: '#4db8b8', document: '#c8a96e', code: '#9b8fc4',
    data: '#7a9e82', image: '#888', analysis: '#c8a96e',
  };

  async function submitFeedback() {
    if (!feedbackRating || feedbackSending) return;
    setFeedbackSending(true);
    try {
      await supabase.from('feedback').insert({
        user_id: userId,
        session_id: currentSessionId,
        rating: feedbackRating,
        note: feedbackNote.trim() || null,
        created_at: new Date().toISOString(),
      });
      setFeedbackSent(true);
      setTimeout(() => {
        setFeedbackSent(false);
        setFeedbackRating(null);
        setFeedbackNote('');
        setFeedbackSending(false);
      }, 2000);
    } catch (e) {
      console.error('submitFeedback:', e);
      setFeedbackSending(false);
    }
  }

  function ArtifactCard({ artifact }) {
    const [hovered, setHovered] = useState(false);
    const typeColor = TYPE_COLORS[(artifact.artifact_type || '').toLowerCase()] || '#888';
    async function archiveArtifact(e) {
      e.stopPropagation();
      await supabase.from('artifacts').update({ is_archived: true }).eq('id', artifact.id);
      setThreadArtifacts(prev => prev.filter(a => a.id !== artifact.id));
      setPinnedArtifacts(prev => prev.filter(a => a.id !== artifact.id));
      setRecentArtifacts(prev => prev.filter(a => a.id !== artifact.id));
    }
    return (
      <div
        className="shift-card"
        onClick={() => { onArtifactSelect?.(); setSelectedArtifact(artifact); setViewerTab('preview'); }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ marginBottom: 4, cursor: 'pointer', position: 'relative' }}
      >
        {hovered && (
          <div
            onClick={archiveArtifact}
            style={{
              position: 'absolute', top: 8, right: 8,
              cursor: 'pointer', color: '#444',
              display: 'flex', alignItems: 'center',
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#c46050'}
            onMouseLeave={e => e.currentTarget.style.color = '#444'}
          >
            <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
              <path d="M2 4h12M5 4V2h6v2M6 7v5M10 7v5M3 4l1 10h8l1-10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
          <span style={{ fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: typeColor }}>
            {artifact.artifact_type || 'artifact'}
          </span>
          <span style={{ fontSize: 10, color: 'var(--text-7)' }}>
            {artifact.created_at ? new Date(artifact.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
          </span>
        </div>
        <div style={{ color: 'var(--text-2)', fontSize: 12, fontWeight: 'bold', marginBottom: 2, lineHeight: 1.3 }}>
          {artifact.title}
        </div>
        {artifact.description && (
          <div style={{ color: 'var(--text-7)', fontSize: 11, lineHeight: 1.5 }}>
            {artifact.description}
          </div>
        )}
      </div>
    );
  }

  // ATTACHMENT VIEWER
  if (selectedAttachment) {
    const attachUrl = supabase.storage.from('shift-files').getPublicUrl(selectedAttachment.storage_path)?.data?.publicUrl;
    return (
      <div style={{ width, borderLeft: '1px solid var(--border-4)', background: 'var(--surface-1)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid var(--border-4)' }}>
          <div onClick={() => setSelectedAttachment(null)} style={{ cursor: 'pointer', color: 'var(--text-7)', fontSize: 11, letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 4 }}>
            <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Back
          </div>
        </div>
        <div style={{ padding: '12px 12px 4px' }}>
          <div style={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#888', marginBottom: 6 }}>{selectedAttachment.file_type?.toUpperCase() || 'FILE'}</div>
          <div style={{ fontSize: 13, color: 'var(--text-2)', fontWeight: 'bold', lineHeight: 1.4 }}>{selectedAttachment.file_name}</div>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
          {selectedAttachment.is_image && attachUrl ? (
            <img src={attachUrl} alt={selectedAttachment.file_name} style={{ width: '100%', borderRadius: 6, display: 'block' }} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12 }}>
              <span style={{ fontSize: 48 }}>📄</span>
              <div style={{ fontSize: 13, color: 'var(--text-5)', textAlign: 'center' }}>{selectedAttachment.file_name}</div>
              <div onClick={() => attachUrl && window.open(attachUrl, '_blank')} style={{ fontSize: 12, color: '#7a9e82', cursor: 'pointer', letterSpacing: '0.06em', border: '1px solid rgba(122,158,130,0.3)', padding: '6px 14px', borderRadius: 3 }}>open file</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // VIEWER STATE
  if (selectedArtifact) {
    return (
      <div style={{ width, borderLeft: '1px solid var(--border-4)', background: 'var(--surface-1)', display: 'flex', flexDirection: 'column', flexShrink: 0, transition: 'width 0.2s ease' }}>
        <div style={{ padding: '8px 14px', borderBottom: '1px solid var(--border-4)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div
            onClick={() => setSelectedArtifact(null)}
            style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--text-6)', fontSize: 11, fontFamily: 'var(--font-serif)', cursor: 'pointer' }}
          >
            <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Back
          </div>
          <span style={{ color: 'var(--text-7)', fontSize: 10, border: '1px solid var(--border-3)', borderRadius: 4, padding: '2px 6px', fontFamily: 'var(--font-serif)' }}>
            v{selectedArtifact.version_count || 1}
          </span>
        </div>
        <div style={{ padding: '8px 14px', borderBottom: '1px solid var(--border-4)', flexShrink: 0 }}>
          <div style={{ color: 'var(--text-2)', fontSize: 13, fontWeight: 'bold', marginBottom: 2 }}>{selectedArtifact.title}</div>
          <div style={{ color: 'var(--text-7)', fontSize: 10 }}>{selectedArtifact.artifact_type} · from this thread</div>
        </div>
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border-4)', flexShrink: 0 }}>
          {['preview', 'code'].map(t => (
            <div
              key={t}
              onClick={() => setViewerTab(t)}
              style={{ flex: 1, padding: '8px', fontSize: 12, fontFamily: 'var(--font-serif)', color: viewerTab === t ? '#c8a96e' : 'var(--text-6)', borderBottom: `2px solid ${viewerTab === t ? '#c8a96e' : 'transparent'}`, cursor: 'pointer', textAlign: 'center', textTransform: 'capitalize' }}
            >
              {t}
            </div>
          ))}
        </div>
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {viewerTab === 'preview' ? <PreviewTab artifact={selectedArtifact} /> : <CodeTab artifact={selectedArtifact} />}
        </div>
      </div>
    );
  }

  // LIST STATE
  return (
    <div style={{ width, borderLeft: '1px solid var(--border-4)', background: 'var(--surface-1)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border-4)', flexShrink: 0 }}>
        {['artifacts', 'highlights', 'feedback'].map(t => (
          <div
            key={t}
            onClick={() => setActiveTab(t)}
            style={{ flex: 1, padding: '10px 8px', fontSize: 12, fontFamily: 'var(--font-serif)', color: activeTab === t ? '#c8a96e' : 'var(--text-6)', borderBottom: `2px solid ${activeTab === t ? '#c8a96e' : 'transparent'}`, cursor: 'pointer', textAlign: 'center', textTransform: 'capitalize' }}
          >
            {t}
          </div>
        ))}
      </div>

      {activeTab === 'artifacts' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          {loading ? (
            <div style={{ color: 'var(--text-7)', fontSize: 12, fontFamily: 'var(--font-serif)', textAlign: 'center', padding: '20px 0', fontStyle: 'italic' }}>loading···</div>
          ) : (
            <>
              {(() => {
                const uniqueThread = threadArtifacts.filter((a, i, self) => i === self.findIndex(b => b.content === a.content));
                return uniqueThread.length > 0 ? (
                  <>
                    <div style={{ fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-6)', padding: '10px 14px 6px', fontFamily: 'var(--font-serif)' }}>This session</div>
                    {uniqueThread.map(a => <ArtifactCard key={a.id} artifact={a} />)}
                  </>
                ) : null;
              })()}
              {pinnedArtifacts.length > 0 && (
                <>
                  <div style={{ fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-6)', padding: '10px 14px 6px', fontFamily: 'var(--font-serif)' }}>Pinned</div>
                  {pinnedArtifacts.map(a => <ArtifactCard key={a.id} artifact={a} />)}
                </>
              )}
              {recentArtifacts.length > 0 && (
                <>
                  <div style={{ fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-6)', padding: '10px 14px 6px', fontFamily: 'var(--font-serif)' }}>Recent</div>
                  {recentArtifacts.map(a => <ArtifactCard key={a.id} artifact={a} />)}
                </>
              )}
              {threadArtifacts.length === 0 && pinnedArtifacts.length === 0 && recentArtifacts.length === 0 && (
                <div style={{ color: 'var(--text-7)', fontSize: 12, fontFamily: 'var(--font-serif)', textAlign: 'center', padding: '40px 16px', fontStyle: 'italic' }}>
                  No artifacts yet. Shift will save them here automatically.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'highlights' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
          {highlights.length === 0 ? (
            <div style={{ color: 'var(--text-7)', fontSize: 12, fontFamily: 'var(--font-serif)', textAlign: 'center', padding: '40px 16px', fontStyle: 'italic' }}>
              Select any text in the conversation to capture a highlight.
            </div>
          ) : (
            highlights.map((h) => (
              <div
                key={h.id}
                onMouseEnter={() => setHoveredHighlight(h.id)}
                onMouseLeave={() => setHoveredHighlight(null)}
                style={{ padding: '10px 12px', borderRadius: 7, borderLeft: `3px solid ${HIGHLIGHT_COLOR}`, background: 'var(--surface-3)', marginBottom: 6, position: 'relative' }}
              >
                {hoveredHighlight === h.id && (
                  <div
                    onClick={(e) => { e.stopPropagation(); deleteHighlight(h.id); }}
                    style={{
                      position: 'absolute', top: 8, right: 8,
                      cursor: 'pointer', color: '#444',
                      display: 'flex', alignItems: 'center',
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = HIGHLIGHT_COLOR}
                    onMouseLeave={e => e.currentTarget.style.color = '#444'}
                  >
                    <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
                      <path d="M2 4h12M5 4V2h6v2M6 7v5M10 7v5M3 4l1 10h8l1-10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
                <div style={{ color: 'var(--text-3)', fontSize: 12, fontStyle: 'italic', lineHeight: 1.6, marginBottom: 6 }}>
                  {h.text}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-7)', fontSize: 10, fontFamily: 'var(--font-serif)' }}>
                    {h.created_at ? new Date(h.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 6px' }}>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: HIGHLIGHT_COLOR, flexShrink: 0 }} />
                    <span style={{ fontSize: 10, color: HIGHLIGHT_COLOR, fontFamily: 'var(--font-serif)' }}>Highlight</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'feedback' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '20px 16px', gap: 14 }}>
          {feedbackSent ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid #7a9e8255', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8l3.5 3.5L13 4.5" stroke="#7a9e82" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <div style={{ color: '#555', fontSize: 13, fontFamily: 'var(--font-serif)', fontStyle: 'italic' }}>Feedback received</div>
              <div style={{ color: '#2a2a2a', fontSize: 11, fontFamily: 'var(--font-serif)' }}>Resets in a moment...</div>
            </div>
          ) : (
            <>
              <div>
                <div style={{ color: '#2a2a2a', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10, fontFamily: 'var(--font-serif)' }}>
                  Was this session useful?
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[
                    { id: 'yes', label: 'Yes', activeColor: '#7a9e82', activeBg: 'rgba(122,158,130,0.06)', activeBorder: '#7a9e8255' },
                    { id: 'somewhat', label: 'Somewhat', activeColor: '#c8a96e', activeBg: 'rgba(200,169,110,0.06)', activeBorder: '#c8a96e55' },
                    { id: 'no', label: 'No', activeColor: '#c46050', activeBg: 'rgba(196,96,80,0.06)', activeBorder: '#c4605055' },
                  ].map(opt => (
                    <div
                      key={opt.id}
                      onClick={() => setFeedbackRating(opt.id)}
                      style={{
                        flex: 1, padding: '8px 4px', borderRadius: 6, cursor: 'pointer', textAlign: 'center',
                        fontFamily: 'var(--font-serif)', fontSize: 12,
                        border: `1px solid ${feedbackRating === opt.id ? opt.activeBorder : '#272736'}`,
                        color: feedbackRating === opt.id ? opt.activeColor : '#444',
                        background: feedbackRating === opt.id ? opt.activeBg : 'none',
                      }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <textarea
                  value={feedbackNote}
                  onChange={e => setFeedbackNote(e.target.value)}
                  placeholder="Anything to add? (optional)"
                  rows={3}
                  style={{
                    width: '100%', background: '#111', border: '1px solid #272736',
                    borderRadius: 6, color: '#888', fontFamily: 'var(--font-serif)',
                    fontSize: 12, padding: '8px 10px', resize: 'none', outline: 'none', lineHeight: 1.6,
                  }}
                />
              </div>
              <button
                onClick={submitFeedback}
                disabled={!feedbackRating}
                style={{
                  width: '100%', padding: 9, borderRadius: 7,
                  border: `1px solid ${feedbackRating ? '#c8a96e33' : '#272736'}`,
                  background: 'none',
                  color: feedbackRating ? '#c8a96e' : '#333',
                  fontFamily: 'var(--font-serif)', fontSize: 12, cursor: feedbackRating ? 'pointer' : 'default',
                  opacity: feedbackSending ? 0.5 : 1,
                }}
              >
                {feedbackSending ? 'Sending...' : 'Send'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}